import { useFileInput } from "../../../../hooks/useFileInput";
import { useMutation } from "../../../../shared/hooks/useMutation";
import { isErrors, isValue } from "@me8eon/errors";
import React, { useCallback, useContext } from "react";
import { AiClient } from "@me8eon/aiclient";
import { fileToDataUrl } from "../../../../util/fileToDataUrl";
import { AiAssistContext } from "../../../../components/ai-assist-refactored/AiAssist.context";

type Props = {
    ai: AiClient
    setSuggestions: (suggestions: string[]) => void;
}

const ACCEPTED_FILE_TYPES = [".jpg", ".jpeg", ".png"];

const suggestionsPrompt = `
    ## Role
    You're an assistant for users who upload images to get suggestions on what to ask about the image.
        
    ## Context
    User uploads an image and you need to analyze the image and provide suggestions on what the user can ask about the image. The suggestions should be related to the issue that user might have based on the image content, not about the content itself. For example, if user uploads a screenshot of an error message, you should provide suggestions on how to fix the issue, not what is shown in the error message.
    
    ## Instructions
    - Analyze the image and understand the context of the issue that user might have.
    - Provide 2-3 suggestions on what the user can ask about the image. The suggestions should be related to the issue that user might have based on the image content, not about the content itself. For example, if user uploads a screenshot of an error message, you should provide suggestions on how to fix the issue, not what is shown in the error message.
    - The suggestions should be concise and clear.
    - Provide response in JSON format with the following structure:
        {
            "suggestions": [
                ...suggestions
            ]
        }
        
    ## Examples:
    (Context: User uploads a screenshot of an error message related to microphone access in Windows 10)
    
    **Good suggestions:**
    - How to fix microphone access issues in Windows 10?
    - Troubleshooting microphone problems in Windows 10.
    
    **Bad suggestions:**
    - What is shown in the error message?
    - Describe the content of the image.`

export const useImageUploaderTextAreaPlugin = ({ ai, setSuggestions }: Props) => {
    const fileInput = useFileInput();
    const { incidentFiles, setIncidentFiles } = useContext(AiAssistContext);
    const mutation = useMutation((dataUrl: string) => ai([
        {
            role: "user",
            content: [
                { type: "text", text: suggestionsPrompt },
                { type: "image_url", image_url: { url: dataUrl } }
            ]
        }
    ], {
        response_format: {
            type: "json_object"
        }
    }).then((response) => {
        if (isErrors(response)) {
            throw new Error("AI response error: " + response.errors.map(e => e).join(", "));
        }
        if (isValue(response)) {
            const content = response.value.message.content
            if (typeof content === "string") {
                const parsed = JSON.parse(content);
                if (parsed && Array.isArray(parsed.suggestions)) {
                    setSuggestions(parsed.suggestions);
                    return;
                } else {
                    throw new Error("Parsed content does not contain suggestions array");
                }
            } else {
                throw new Error("Unexpected content format in AI response");
            }
        }
        setSuggestions([]);
    }).catch((error) => {
        setSuggestions([]);
        throw new Error("Failed to process AI response: " + (error instanceof Error ? error.message : String(error)));
    }));

    const onIconClick = useCallback(fileInput.handleIconClick({
        type: "file",
        accept: ACCEPTED_FILE_TYPES.join(","),
    }, async (files) => {
        if (!files?.length) return;
        const file = files[0];
        const reader = new FileReader();
        reader.onloadend = () => {
            const base64String = (reader.result as string).split(',')[1];
            setIncidentFiles([...incidentFiles, {
                file_name: file.name,
                size: file.size,
                file_data: base64String
            }]);
        };
        reader.readAsDataURL(file);
        const dataUrl = await fileToDataUrl(file);
        setSuggestions([]);

        void mutation.mutate(dataUrl);
    }), [fileInput.handleIconClick, mutation.mutate]);

    const handleRemoveFile = useCallback((e: React.MouseEvent, file: File) => {
        fileInput.handleRemoveFile(e, file);
        setSuggestions([]);
    }, [fileInput.handleRemoveFile, setSuggestions]);

    return { onIconClick, ...fileInput, ...mutation, handleRemoveFile };
}